import svgCrosswordGenerator from './svgCrosswordGenerator';
import generateHuntingWordsSVG from './svgGenerator';
import generateGameSVG from './generateGameSvg';
import generateFencesSVG from './generateFencesSvg';
import generateGridText from './generateGridText';
import { buildLetrexEnigmaTable } from './letrexHints';

export const GAME_TYPES = {
  CROSSWORD: 'crossword',
  WORD_SEARCH: 'wordsearch',
  SHAPE_LINKS: 'shapelinks',
  LOOP: 'loop',
  NUMBER_SQUARE: 'numberSquare',
  FENCES: 'fences',
  WALLS: 'walls',
  LETREX: 'letrex',
  NUMEREX: 'numerex',
};

const TOGGLE_OPTIONS = {
  colored: { key: 'colored', label: 'shared.export.toggles.colored' },
  transparentBg: {
    key: 'transparentBackground',
    label: 'shared.export.toggles.transparentBg',
  },
  exportBank: { key: 'exportBank', label: 'shared.export.toggles.exportBank' },
  exportWithHints: {
    key: 'exportWithHints',
    label: 'shared.export.toggles.exportWithHints',
  },
};

const RADIO_GROUPS = {
  arrowStyle: {
    key: 'arrowHeadStyle',
    label: 'shared.export.radio.arrowStyle.label',
    choices: [
      { value: 'default', label: 'shared.export.radio.arrowStyle.triangle' },
      { value: 'filled', label: 'shared.export.radio.arrowStyle.filledCircle' },
      { value: 'empty', label: 'shared.export.radio.arrowStyle.emptyCircle' },
      { value: 'dotted', label: 'shared.export.radio.arrowStyle.dottedCircle' },
    ],
  },
  pathWeight: {
    key: 'exportPathWeight',
    label: 'shared.export.radio.pathWeight.label',
    choices: [
      { value: 'thin', label: 'shared.export.radio.pathWeight.thin' },
      { value: 'normal', label: 'shared.export.radio.pathWeight.normal' },
      { value: 'bold', label: 'shared.export.radio.pathWeight.bold' },
    ],
  },
};

export const EXPORT_CONFIG = {
  [GAME_TYPES.CROSSWORD]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: 'r',
        defaultSelected: true,
      },
      combined: {
        label: 'shared.export.types.combined',
        suffix: '_completo',
        defaultSelected: false,
      },
    },
    toggles: [TOGGLE_OPTIONS.transparentBg, TOGGLE_OPTIONS.exportBank],
    radioGroups: [RADIO_GROUPS.arrowStyle],
    useIndexNaming: false,
    generateSvg: (item, typeKey, options) => {
      const modeMap = { enigma: 'enigma', solution: 'solution', combined: 'combined' };
      const config = {
        ...item.settings, ...options, exportMode: modeMap[typeKey], bank: item.bank || [], bankPosition: 'right',
      };
      return svgCrosswordGenerator(item.table, item.rows, item.columns, config);
    },
  },

  [GAME_TYPES.WORD_SEARCH]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '_Enigma',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: '_Solucao',
        defaultSelected: true,
      },
      listWord: {
        label: 'shared.export.types.wordList',
        suffix: '_Palavras',
        isText: true,
        defaultSelected: true,
        generateText: item => {
          const words = item.highlightedWords?.map(w => w.word)
            .sort((a, b) => a.localeCompare(b)) || [];

          return words.join('\n');
        },
      },
      gridTxt: {
        label: 'shared.export.types.gridTxt',
        suffix: '_Grid',
        isText: true,
        defaultSelected: true,
        generateText: item => generateGridText(item.table, item.rows, item.columns),
      },
    },
    toggles: [],
    radioGroups: [],
    useIndexNaming: true,
    generateSvg: (item, typeKey) => {
      if (typeKey === 'enigma') {
        return generateHuntingWordsSVG(item.table, item.rows, item.columns);
      }
      return generateHuntingWordsSVG(item.table, item.rows, item.columns, {
        highlightedWords: item.highlightedWords,
      });
    },
  },

  [GAME_TYPES.SHAPE_LINKS]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '_Enigma',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: '_Solucao',
        defaultSelected: true,
      },
    },
    toggles: [TOGGLE_OPTIONS.colored, TOGGLE_OPTIONS.transparentBg],
    radioGroups: [RADIO_GROUPS.pathWeight],
    useIndexNaming: false,
    generateSvg: (item, typeKey, options) => {
      const config = {
        ...item.settings,
        colored: options.colored,
        pathWeight: options.exportPathWeight,
        transparentBackground: options.transparentBackground,
        showAnswer: typeKey === 'solution',
      };
      return generateGameSVG(item.table, item.rows, item.columns, config);
    },
  },

  [GAME_TYPES.LOOP]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '_Enigma',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: '_Solucao',
        defaultSelected: true,
      },
    },
    toggles: [TOGGLE_OPTIONS.colored, TOGGLE_OPTIONS.transparentBg],
    radioGroups: [RADIO_GROUPS.pathWeight],
    generateSvg: (item, typeKey, options) => {
      const config = {
        ...item.settings,
        colored: options.colored,
        pathWeight: options.exportPathWeight,
        transparentBackground: options.transparentBackground,
        showAnswer: typeKey === 'solution',
      };

      return generateGameSVG(item.table, item.rows, item.columns, config);
    },
  },

  [GAME_TYPES.NUMBER_SQUARE]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '_Enigma',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: '_Solucao',
        defaultSelected: true,
      },
    },
    toggles: [TOGGLE_OPTIONS.transparentBg],
    radioGroups: [],
    generateSvg: (item, typeKey, options) => {
      const config = {
        ...item.settings,
        colored: options.colored,
        pathWeight: options.exportPathWeight,
        transparentBackground: options.transparentBackground,
        showAnswer: typeKey === 'solution',
      };

      return generateGameSVG(item.table, item.rows, item.columns, config);
    },
  },

  [GAME_TYPES.FENCES]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '_Enigma',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: '_Solucao',
        defaultSelected: true,
      },
    },
    toggles: [TOGGLE_OPTIONS.colored, TOGGLE_OPTIONS.transparentBg],
    radioGroups: [],
    generateSvg: (item, typeKey, options) => {
      const config = {
        ...item.settings,
        colored: options.colored,
        pathWeight: options.exportPathWeight,
        transparentBackground: options.transparentBackground,
        showAnswer: typeKey === 'solution',
      };

      return generateFencesSVG(item.table, item.rows, item.columns, config);
    },
  },

  [GAME_TYPES.WALLS]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '_Enigma',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: '_Solucao',
        defaultSelected: true,
      },
    },
    toggles: [TOGGLE_OPTIONS.transparentBg],
    radioGroups: [],
    generateSvg: (item, typeKey, options) => {
      const config = {
        ...item.settings,
        colored: options.colored,
        pathWeight: options.exportPathWeight,
        transparentBackground: options.transparentBackground,
        showAnswer: typeKey === 'solution',
      };

      return generateGameSVG(item.table, item.rows, item.columns, config);
    },
  },

  [GAME_TYPES.LETREX]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '_Enigma',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: '_Solucao',
        defaultSelected: true,
      },
      listWord: {
        label: 'shared.export.types.wordList',
        suffix: '_Palavras',
        isText: true,
        defaultSelected: true,
        generateText: item => item.settings?.words_list || '',
      },
      gridTxt: {
        label: 'shared.export.types.gridTxt',
        suffix: '_Grid',
        isText: true,
        defaultSelected: false,
        generateText: item => generateGridText(item.table, item.rows, item.columns, {
          emptyChar: '.',
          separator: '',
          rowKey: 'positiony',
          colKey: 'positionx',
        }),
      },
    },
    toggles: [],
    typeExtras: [
      {
        ...TOGGLE_OPTIONS.exportWithHints,
        afterType: 'enigma',
        default: false,
      },
    ],
    radioGroups: [],
    useIndexNaming: false,
    generateSvg: (item, typeKey, options = {}) => {
      const svgOptions = {
        showCellBorders: true,
        showCellBackground: true,
        hideEmptyCells: true,
        swapAxes: false,
        borderColor: '#000000',
        borderWidth: 2,
      };

      if (typeKey === 'enigma') {
        const table = buildLetrexEnigmaTable(item, {
          withHints: Boolean(options.exportWithHints),
        });
        return generateHuntingWordsSVG(table, item.rows, item.columns, svgOptions);
      }

      return generateHuntingWordsSVG(item.table, item.rows, item.columns, svgOptions);
    },
  },

  [GAME_TYPES.NUMEREX]: {
    types: {
      enigma: {
        label: 'shared.export.types.enigma',
        suffix: '_Enigma',
        defaultSelected: true,
      },
      solution: {
        label: 'shared.export.types.solution',
        suffix: '_Solucao',
        defaultSelected: true,
      },
      listNumber: {
        label: 'shared.export.types.numberList',
        suffix: '_Numeros',
        isText: true,
        defaultSelected: true,
        generateText: item => item.settings?.numbers_list || '',
      },
      gridTxt: {
        label: 'shared.export.types.gridTxt',
        suffix: '_Grid',
        isText: true,
        defaultSelected: false,
        generateText: item => generateGridText(item.table, item.rows, item.columns, {
          emptyChar: '.',
          separator: ' ',
          rowKey: 'positiony',
          colKey: 'positionx',
        }),
      },
    },
    toggles: [],
    radioGroups: [],
    useIndexNaming: false,
    generateSvg: (item, typeKey) => {
      const svgOptions = {
        showCellBorders: true,
        showCellBackground: true,
        hideEmptyCells: true,
        swapAxes: false,
        borderColor: '#000000',
        borderWidth: 2,
      };

      if (typeKey === 'enigma') {
        const table = (item.table || []).map(cell => {
          const hasDigit =
            cell.text != null && String(cell.text).trim() !== '';
          return {
            ...cell,
            text: hasDigit ? '' : null,
          };
        });
        return generateHuntingWordsSVG(table, item.rows, item.columns, svgOptions);
      }

      return generateHuntingWordsSVG(item.table, item.rows, item.columns, svgOptions);
    },
  },
};
